import { useEffect, useRef, useState } from "react";
import { FiChevronLeft, FiHeart, FiDownload, FiSend, FiTrash2, FiVolume2, FiVolumeX, FiChevronUp, FiChevronDown, FiCopy, FiMusic, FiClock, FiEdit2, FiLock } from "react-icons/fi";
import toast from "react-hot-toast";
import { pickTracksForClip, toUiTracks } from "../../utils/musicMatch";
import "./ClipFeedViewer.css";

// Format seconds → m:ss.
function fmtClock(sec) {
  if (sec == null || !Number.isFinite(sec)) return null;
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * Full-screen, TikTok-style vertical clip feed. Swipe up/down (scroll-snap)
 * between clips; the in-view clip autoplays. Right-side rail = like, download,
 * publish, delete (icons only). Tap the caption to expand AI caption (copyable),
 * music pick, and timecode.
 *
 * Props:
 *   clips      [{ clip, job, clipIndex }]
 *   startIndex number
 *   likedIds   string[]
 *   catalog    music catalog (for the matched-track detail)
 *   onClose, onLike(key), onDownload, onPublish, onDelete
 */
// locked (free run, not Pro): clips play, but Save / Edit / Post call onLocked (the paywall).
export default function ClipFeedViewer({ clips, startIndex = 0, onClose, likedIds = [], catalog = [], onLike, onDownload, onPublish, onDelete, locked = false, onLocked }) {
  const feedRef = useRef(null);
  const slideRefs = useRef([]);
  const videoRefs = useRef([]);
  const touchRef = useRef(null); // swipe-right to exit back to Library
  const [muted, setMuted] = useState(true);
  const [openDetails, setOpenDetails] = useState(null); // clip key whose details sheet is expanded
  const [downloadingKey, setDownloadingKey] = useState(null); // clip key currently downloading (spinner on Save)
  const [activeIndex, setActiveIndex] = useState(startIndex); // which slide is in view
  const [buffering, setBuffering] = useState(true); // active clip still loading → show spinner not black

  // Jump to the tapped clip on open.
  useEffect(() => {
    const el = feedRef.current;
    if (el) el.scrollTop = startIndex * el.clientHeight;
    setActiveIndex(startIndex);
  }, [startIndex]);

  // Observe the slide wrappers (always mounted) to know which clip is in view.
  // Only the in-view clip's <video> is mounted (iOS caps concurrent <video>
  // elements ~16, which is why a full feed of videos stalls/blacks out).
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio > 0.6) {
            const idx = Number(e.target.dataset.index);
            if (!Number.isNaN(idx)) { setActiveIndex(idx); setBuffering(true); }
          }
        });
      },
      { threshold: [0, 0.6, 1] }
    );
    slideRefs.current.forEach((s) => s && obs.observe(s));
    return () => obs.disconnect();
  }, [clips]);

  // Play only the active clip; pause the rest. Force muted before play() so
  // mobile honors autoplay (audible autoplay is blocked). Kick a load() on the
  // active one so its GCS fetch starts immediately.
  useEffect(() => {
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === activeIndex) {
        v.muted = muted;
        try { if (v.networkState === v.NETWORK_EMPTY || v.readyState === 0) v.load(); } catch { /* ignore */ }
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      } else {
        try { v.pause(); } catch { /* ignore */ }
      }
    });
  }, [activeIndex, muted, clips]);

  // Esc closes.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!clips?.length) return null;
  const noop = { stopPropagation() {} };

  const copyCaption = (text) => {
    if (!text) return;
    try { navigator.clipboard.writeText(text); toast.success("Caption copied"); }
    catch { toast.error("Copy failed"); }
  };

  // Smoothly scroll the feed to a given clip (desktop up/down arrows).
  const scrollToIndex = (i) => {
    const el = feedRef.current;
    if (!el || i < 0 || i >= clips.length) return;
    el.scrollTo({ top: i * el.clientHeight, behavior: "smooth" });
  };

  // Editing runs in the desktop /editor bundle. On mobile, tell the user.
  const isMobile = typeof window !== "undefined" &&
    (window.matchMedia?.("(max-width: 820px)")?.matches ||
     /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || ""));
  const handleEdit = (job, clipIndex) => {
    if (isMobile) { toast("Editing is available on desktop"); return; }
    window.location.href = `/edit/${job._id}/${clipIndex}`;
  };

  return (
    <div className="cfv-overlay" data-paywall-allow={locked ? "" : undefined}>
      <button className="cfv-top-btn cfv-back" onClick={onClose} aria-label="Back"><FiChevronLeft /></button>
      <button className="cfv-top-btn cfv-mute" onClick={() => setMuted((m) => !m)} aria-label="Sound">
        {muted ? <FiVolumeX /> : <FiVolume2 />}
      </button>

      {/* Desktop up/down navigation (mobile just swipes). */}
      {locked && (
        <button type="button" className="cfv-unlock" onClick={() => onLocked?.()}>
          <FiLock /> Unlock Pro to download
        </button>
      )}
      <div className="cfv-nav">
        <button className="cfv-nav-btn" onClick={() => scrollToIndex(activeIndex - 1)} disabled={activeIndex === 0} aria-label="Previous clip"><FiChevronUp /></button>
        <button className="cfv-nav-btn" onClick={() => scrollToIndex(activeIndex + 1)} disabled={activeIndex === clips.length - 1} aria-label="Next clip"><FiChevronDown /></button>
      </div>

      <div
        className="cfv-feed"
        ref={feedRef}
        onTouchStart={(e) => { const t = e.touches[0]; touchRef.current = { x: t.clientX, y: t.clientY }; }}
        onTouchEnd={(e) => {
          if (!touchRef.current) return;
          const t = e.changedTouches[0];
          const dx = t.clientX - touchRef.current.x;
          const dy = t.clientY - touchRef.current.y;
          touchRef.current = null;
          // Swipe right (mostly horizontal) exits to the Library view.
          if (dx > 70 && Math.abs(dx) > Math.abs(dy) * 1.5) onClose?.();
        }}
      >
        {clips.map(({ clip, job, clipIndex }, i) => {
          const key = `${job._id}-${clipIndex}`;
          const liked = likedIds.includes(key);
          const expanded = openDetails === key;

          const captionText = clip.caption || clip.hook || "";
          const startSec = clip.start_sec ?? clip.start ?? clip.broadcastStartSec ?? null;
          const dur = Number.isFinite(clip.duration) ? clip.duration : null;
          const startStr = fmtClock(startSec);
          const endStr = startSec != null && dur != null ? fmtClock(startSec + dur) : null;
          const timecode = startStr ? (endStr ? `${startStr} – ${endStr}` : startStr) : (dur != null ? `${Math.round(dur)}s` : null);

          // Only match music when the details sheet is open (matching is not free).
          let musicLabel = null;
          if (expanded && catalog?.length) {
            try {
              const m = pickTracksForClip(catalog, clip, 1);
              const ui = toUiTracks(m.tracks || []);
              if (ui[0]) musicLabel = ui[0].artist ? `${ui[0].trackName} — ${ui[0].artist}` : ui[0].trackName;
            } catch { /* ignore */ }
          }

          const poster = clip.thumbnailUrl
            ? `/api/clips/thumb?u=${encodeURIComponent(clip.thumbnailUrl)}`
            : (job.videoThumbnail || undefined);

          // Virtualize: only mount the <video> for the active clip + its
          // immediate neighbors. Far slides render just a poster image, so the
          // DOM never holds more than ~3 <video> elements (iOS caps them, and a
          // full feed of videos is what made playback slow/black + exit laggy).
          const inWindow = Math.abs(i - activeIndex) <= 1;
          const isActive = i === activeIndex;

          return (
            <div className="cfv-slide" key={clip._id || key} data-index={i} ref={(el) => (slideRefs.current[i] = el)}>
              {inWindow ? (
                <video
                  ref={(el) => (videoRefs.current[i] = el)}
                  className="cfv-video"
                  data-index={i}
                  src={clip.editedUrl || clip.url}
                  poster={poster}
                  playsInline
                  webkit-playsinline="true"
                  loop
                  muted={muted}
                  preload={isActive ? "auto" : "metadata"}
                  onWaiting={() => { if (isActive) setBuffering(true); }}
                  onPlaying={() => { if (isActive) setBuffering(false); }}
                  onCanPlay={() => { if (isActive) setBuffering(false); }}
                  onLoadedData={() => { if (isActive) setBuffering(false); }}
                  onTimeUpdate={() => { if (isActive && buffering) setBuffering(false); }}
                  onClick={(e) => { const v = e.currentTarget; v.paused ? v.play().catch(() => {}) : v.pause(); }}
                />
              ) : (
                poster ? <img className="cfv-video" src={poster} alt="" draggable={false} /> : <div className="cfv-video" />
              )}
              {isActive && buffering && (
                <div className="cfv-buffering"><span className="cfv-spinner" /></div>
              )}

              <div className="cfv-actions">
                <button className={`cfv-action${liked ? " liked" : ""}`} onClick={() => onLike?.(key)} aria-label="Like">
                  <FiHeart fill={liked ? "currentColor" : "none"} />
                </button>
                <button
                  className={`cfv-action${downloadingKey === key ? " cfv-action--busy" : ""}`}
                  disabled={downloadingKey === key}
                  onClick={async () => {
                    if (locked) { onLocked?.(); return; }
                    setDownloadingKey(key);
                    const startedAt = Date.now();
                    try {
                      await onDownload?.(clip, job, clipIndex, noop);
                    } finally {
                      // keep the spinner visible at least ~700ms so the tap registers
                      const wait = Math.max(0, 700 - (Date.now() - startedAt));
                      setTimeout(() => setDownloadingKey((k) => (k === key ? null : k)), wait);
                    }
                  }}
                  aria-label="Save"
                >
                  {downloadingKey === key ? <span className="cfv-dl-spin" /> : locked ? <FiLock /> : <FiDownload />}
                </button>
                <button className="cfv-action" onClick={() => (locked ? onLocked?.() : handleEdit(job, clipIndex))} aria-label="Edit">
                  {locked ? <FiLock /> : <FiEdit2 />}
                </button>
                <button className="cfv-action" onClick={() => (locked ? onLocked?.() : onPublish?.(clip, job, clipIndex, noop))} aria-label="Post">
                  {locked ? <FiLock /> : <FiSend />}
                </button>
                {!locked && (
                  <button className="cfv-action cfv-action--danger" onClick={() => onDelete?.(clip, job, clipIndex, noop)} aria-label="Delete">
                    <FiTrash2 />
                  </button>
                )}
              </div>

              <div className={`cfv-caption${expanded ? " cfv-caption--open" : ""}`}>
                <button className="cfv-caption-head" onClick={() => setOpenDetails(expanded ? null : key)}>
                  <div className="cfv-caption-headtext">
                    <div className="cfv-hook">{clip.hook || "Clip"}</div>
                    {!expanded && (job.videoTitle || job.streamerName) && (
                      <div className="cfv-sub">{job.videoTitle || job.streamerName}</div>
                    )}
                  </div>
                  <span className="cfv-more">
                    {expanded ? <>See less <FiChevronDown /></> : <>See more <FiChevronUp /></>}
                  </span>
                </button>

                {expanded && (
                  <div className="cfv-details">
                    {captionText && (
                      <div className="cfv-detail">
                        <div className="cfv-detail-label">AI caption</div>
                        <div className="cfv-caption-row">
                          <p className="cfv-caption-text">{captionText}</p>
                          <button className="cfv-copy" onClick={() => copyCaption(captionText)} aria-label="Copy caption">
                            <FiCopy /> Copy
                          </button>
                        </div>
                      </div>
                    )}
                    {musicLabel && (
                      <div className="cfv-detail cfv-detail--row">
                        <span className="cfv-detail-ic"><FiMusic /></span>
                        <span className="cfv-detail-val">{musicLabel}</span>
                      </div>
                    )}
                    {timecode && (
                      <div className="cfv-detail cfv-detail--row">
                        <span className="cfv-detail-ic"><FiClock /></span>
                        <span className="cfv-detail-val">{timecode}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
